/**
 * Balls — the only thing redrawn per frame (TBL-001).
 */
import { CUE_BALL } from '@potted/sim';
import type { Viewport } from './viewport.ts';

const BALL_TOKENS = [
  '--ball-1',
  '--ball-2',
  '--ball-3',
  '--ball-4',
  '--ball-5',
  '--ball-6',
  '--ball-7',
  '--ball-8',
  '--ball-9',
  '--ball-10',
] as const;

export interface BallPalette {
  readonly cue: string;
  readonly objects: readonly string[];
  readonly shadow: string;
  /** A thin rim so a ball never melts into the felt; light on dark themes (THM). */
  readonly outline: string;
}

/** Resolved once per re-render; `getComputedStyle` per ball per frame would be slow. */
export function readBallPalette(): BallPalette {
  const style = getComputedStyle(document.documentElement);
  const read = (name: string) => style.getPropertyValue(name).trim();
  return {
    cue: read('--color-cue-ball'),
    objects: BALL_TOKENS.map(read),
    shadow: read('--color-ball-shadow'),
    outline: read('--color-ball-outline'),
  };
}

/**
 * A ball dropping into a pocket, drawn for a moment after it goes down rather than
 * vanishing on the spot (UI-004). Positions only: `sinceDropS` comes from the sampler,
 * which never reveals a drop before it happens.
 */
export interface SinkState {
  /** Per ball: seconds since it dropped, -1 if it has not, +Infinity if long gone. */
  readonly sinceDropS: Float64Array;
  /** How long the sink takes. 0 turns the animation off (reduced motion). */
  readonly durationS: number;
  /** Pocket centres, in table metres, so a ball sinks into the one it went down. */
  readonly pocketsXM: Float64Array;
  readonly pocketsYM: Float64Array;
}

export function drawBalls(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  palette: BallPalette,
  radiusM: number,
  xM: Float64Array,
  yM: Float64Array,
  potted: Uint8Array,
  count: number,
  sink?: SinkState,
): void {
  const fullRadius = viewport.lengthPx(radiusM);

  for (let i = 0; i < count; i++) {
    let bx = xM[i] ?? 0;
    let by = yM[i] ?? 0;
    let radius = fullRadius;
    // 0 on the cloth, rising to 1 as the ball disappears down the pocket.
    let sunk = 0;

    if (potted[i] === 1) {
      const since = sink?.sinceDropS[i] ?? Number.POSITIVE_INFINITY;
      if (sink === undefined || !(since >= 0 && since < sink.durationS)) continue;
      const t = since / sink.durationS;
      // Ease in: it tips over the edge slowly, then drops.
      sunk = t * t;
      const pocket = nearestPocket(sink, bx, by);
      bx += ((sink.pocketsXM[pocket] ?? bx) - bx) * Math.min(1, t * 1.6);
      by += ((sink.pocketsYM[pocket] ?? by) - by) * Math.min(1, t * 1.6);
      radius = fullRadius * (1 - 0.5 * sunk);
    }

    const cx = viewport.toScreenX(bx, by);
    const cy = viewport.toScreenY(bx, by);
    ctx.globalAlpha = 1 - sunk;

    // Contact shadow, offset slightly so the ball reads as sitting ON the cloth.
    // A sinking ball has left the cloth, so it has no shadow.
    if (sunk === 0) {
      ctx.fillStyle = palette.shadow;
      ctx.beginPath();
      ctx.arc(cx + radius * 0.12, cy + radius * 0.16, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    const base =
      i === CUE_BALL
        ? palette.cue
        : (palette.objects[(i - 1) % palette.objects.length] ?? palette.cue);

    ctx.fillStyle = base;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();

    // A single specular highlight, in the same place on every ball — one light source.
    const sheen = ctx.createRadialGradient(
      cx - radius * 0.3,
      cy - radius * 0.35,
      radius * 0.05,
      cx - radius * 0.3,
      cy - radius * 0.35,
      radius * 1.1,
    );
    sheen.addColorStop(0, 'rgb(255 255 255 / 55%)');
    sheen.addColorStop(0.45, 'rgb(255 255 255 / 6%)');
    sheen.addColorStop(1, 'rgb(0 0 0 / 22%)');
    ctx.fillStyle = sheen;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = palette.outline;
    ctx.lineWidth = Math.max(1, radius * 0.09);
    ctx.stroke();

    // Into the dark of the pocket.
    if (sunk > 0) {
      ctx.fillStyle = palette.shadow;
      ctx.globalAlpha = (1 - sunk) * Math.min(1, sunk * 1.5);
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
}

function nearestPocket(sink: SinkState, xM: number, yM: number): number {
  let best = 0;
  let bestD = Number.POSITIVE_INFINITY;
  for (let p = 0; p < sink.pocketsXM.length; p++) {
    const dx = (sink.pocketsXM[p] ?? 0) - xM;
    const dy = (sink.pocketsYM[p] ?? 0) - yM;
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best;
}
